import type React from "react";
import { useEffect, useMemo, useState } from "react";

import { faCircleDollar } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Stack, Typography } from "@mui/material";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { useGetEarnings } from "api/services/admin/hooks";
import type { EarningsView, Professional } from "api/services/admin/schemas";
import { NoGraphData } from "routes/admin/components/NoGraphData";
import { getEndOfMonth, getStartTimeCurrentMonth } from "routes/admin/components/utils";
import {
  ALL_PROFESSIONALS,
  type ProfessionalValue,
  type StatusValue,
  TOTAL_STATUS,
  useFilterOptions,
} from "routes/admin/hooks/useFilterOptions";
import { ButtonsGroup } from "shared/atoms/ButtonsGroup";
import { StackedBarChart } from "shared/atoms/Charts";
import { MUISelect } from "shared/atoms/MUI/MUISelect";
import { GraphSkeleton } from "shared/atoms/SkeletonLayout/components/GraphSkeleton";
import { Spinner } from "shared/atoms/Spinner/Spinner";
import { WidgetBox } from "shared/containers/WidgetBox";
import { convertCentsToDollarsNumber } from "utils/convertCentsToDollars";
import { translateCurrency } from "utils/translations/translateCurrency";

import { useBars } from "./useBars";

interface EarningsProps {
  hcoID: number;
  hcpID?: number;
  professionals: Professional[];
}

export const Earnings: React.FC<EarningsProps> = ({ hcoID, hcpID, professionals }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { defaultMonthValue, monthOptions, statusOptions, professionalOptions } = useFilterOptions(professionals);
  const [view, setView] = useState<EarningsView>("monthly");
  const [selectedProfessional, setSelectedProfessional] = useState<ProfessionalValue>(ALL_PROFESSIONALS);
  const [selectedStatus, setSelectedStatus] = useState<StatusValue>(TOTAL_STATUS);
  const [startTime, setStartTime] = useState(defaultMonthValue);

  const startTimeCurrentMonth = getStartTimeCurrentMonth();

  useEffect(() => {
    setSelectedProfessional(ALL_PROFESSIONALS);
  }, [hcpID]);

  const { data: earningsData, isLoading } = useGetEarnings({
    health_care_organization_id: hcoID,
    params: {
      hcp_id: hcpID || undefined,
      start_time: startTime,
      end_time: getEndOfMonth(startTimeCurrentMonth),
      view,
      therapist_id: selectedProfessional === ALL_PROFESSIONALS ? undefined : selectedProfessional,
    },
  });
  const earnings = earningsData?.earnings || {};

  let currency: string | undefined;
  let itemsExist = false;
  Object.values(earnings).forEach(category => {
    Object.values(category).forEach(item => {
      if (item[selectedStatus] > 0) {
        itemsExist = true;
      }
      if (!currency && item.currency) {
        currency = item.currency;
      }
    });
  });

  const { bars } = useBars();

  const chartData = Object.entries(earnings).map(([date, categories]) => {
    const dataPoint: Record<string, string | number> = { date };

    // Initialize all earning types to 0
    bars.forEach(bar => {
      dataPoint[bar.dataKey] = 0;
    });

    Object.entries(categories).forEach(([key, value]) => {
      dataPoint[key] = value[selectedStatus];
    });

    return dataPoint;
  });

  const stackTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    chartData.forEach(dataPoint => {
      const date = dataPoint.date as string;
      totals[date] = bars.reduce((sum, bar) => sum + (Number(dataPoint[bar.dataKey]) || 0), 0);
    });
    return totals;
  }, [chartData]);

  return (
    <WidgetBox
      title={t("admin.earnings.title")}
      description={t("admin.earnings.description")}
      icon={<FontAwesomeIcon icon={faCircleDollar} />}
      color="purple"
      toolbar={
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <MUISelect
            options={monthOptions}
            disabled={isLoading}
            value={startTime}
            onChange={e => setStartTime(e.target.value as string)}
            size="small"
            sx={{ minWidth: 160 }}
          />
          <MUISelect
            size="small"
            options={professionalOptions}
            disabled={isLoading}
            value={selectedProfessional}
            onChange={event => {
              setSelectedProfessional(event.target.value as ProfessionalValue);
            }}
          />
          <MUISelect
            size="small"
            options={statusOptions}
            disabled={isLoading}
            value={selectedStatus}
            onChange={event => {
              setSelectedStatus(event.target.value as StatusValue);
            }}
          />
          <ButtonsGroup
            group={[
              { label: t("admin.earnings.daily"), value: "daily" },
              { label: t("admin.earnings.weekly"), value: "weekly" },
              { label: t("admin.earnings.monthly"), value: "monthly" },
            ]}
            active={view}
            setActive={value => setView(value as EarningsView)}
          />
          {!isLoading && earningsData?.total !== undefined && (
            <Typography variant="h2">
              {translateCurrency(
                convertCentsToDollarsNumber(earningsData.total[selectedStatus]),
                earningsData.total.currency
              )}
            </Typography>
          )}
          {isLoading && <Spinner small />}
        </Stack>
      }
      skipPadding
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "240px" }}>
        {isLoading && <GraphSkeleton height={340} barCount={view === "daily" ? 30 : 5} />}
        {!isLoading && !itemsExist && <NoGraphData title={t("admin.earnings.no_data")} />}
        {!isLoading && itemsExist && (
          <StackedBarChart
            data={chartData}
            xAxisDataKey="date"
            xAxisProps={{
              tickFormatter: value =>
                format(
                  parseISO(value),
                  view === "monthly" ? t("common.date.longNoDay") : t("common.date.noYear")
                ).toLocaleUpperCase(),
              fontSize: 10,
              interval: view === "daily" ? "equidistantPreserveStart" : "preserveStartEnd",
            }}
            yAxisProps={{
              tickFormatter: value => translateCurrency(convertCentsToDollarsNumber(Number(value)), currency),
            }}
            bars={bars}
            tooltipProps={{
              color: theme.colors.redesign.p100,
              labelFormatter: (label: string) =>
                format(parseISO(label), view === "monthly" ? t("common.date.longNoDay") : t("common.date.long")),
              stackTotals,
              valueFormatter: (value: number) => translateCurrency(convertCentsToDollarsNumber(value), currency),
            }}
          />
        )}
      </Box>
    </WidgetBox>
  );
};
